import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import { Breadcrumb } from '@monorepo/react-components';
import BreadcrumbLink from './breadcrumbs-link';
import { StyledBreadcrumbsContainer } from './styled';

export const BreadcrumbElements: Record<
  PagePath,
  {
    key: string;
    title: string;
    disabled?: boolean;
  }
> = {
  [PagePath.Overview]: {
    title: 'Account Overview',
    key: 'navigation:breadcrumbs.overview',
  },
  [PagePath.Position]: {
    title: 'Position',
    key: 'navigation:breadcrumbs.position',
  },
  [PagePath.Orders]: {
    title: 'Orders',
    key: 'navigation:breadcrumbs.orders',
  },
  [PagePath.Profile]: {
    title: 'Profile',
    key: 'navigation:breadcrumbs.profile',
  },
  [PagePath.Affiliation]: {
    title: 'Affiliation',
    key: 'navigation:breadcrumbs.affiliate',
  },
  [PagePath.AffiliateProgram]: {
    title: 'Affiliate Program',
    key: 'navigation:breadcrumbs.affiliate_program',
  },
  [PagePath.Partners]: {
    title: 'Partners',
    key: 'navigation:breadcrumbs.partners',
  },
  [PagePath.TradingPlan]: {
    title: 'Trading Plan',
    key: 'navigation:breadcrumbs.trading_plan',
  },
  [PagePath.Achievements]: {
    title: 'Achievements',
    key: 'navigation:breadcrumbs.achievements',
  },
  [PagePath.Playbook]: {
    title: 'Playbook',
    key: 'navigation:breadcrumbs.playbook',
  },
  [PagePath.Autochartist]: {
    title: 'Autochartist',
    key: 'navigation:breadcrumbs.autochartist',
  },
  [PagePath.AssetsSpecifications]: {
    title: 'Assets Specifications',
    key: 'navigation:breadcrumbs.assets_specifications',
  },
  [PagePath.Login]: {
    title: 'Login',
    key: 'navigation:breadcrumbs.login',
  },
  [PagePath.Register]: {
    title: 'Register',
    key: 'navigation:breadcrumbs.register',
  },
  [PagePath.RegisterContest]: {
    title: 'Contest Signup',
    key: 'navigation:breadcrumbs.register_contest',
  },
  [PagePath.NewAccount]: {
    title: 'New Account',
    key: 'navigation:breadcrumbs.new_account',
  },
  [PagePath.NewAddon]: {
    title: 'New Addon',
    key: 'navigation:breadcrumbs.new_addon',
  },
  [PagePath.HubDownloads]: {
    title: 'Hub Downloads',
    key: 'navigation:breadcrumbs.hub_downloads',
  },
  [PagePath.HubVod]: {
    title: 'Hub VOD',
    key: 'navigation:breadcrumbs.hub_vod',
  },
  [PagePath.AffiliateKit]: {
    title: 'Affiliate Kit',
    key: 'navigation:breadcrumbs.affiliate_kit',
  },
  [PagePath.PerformanceCoach]: {
    title: 'Performance Coach',
    key: 'navigation:breadcrumbs.performance_coach',
  },
  [PagePath.TradeTalk]: {
    title: 'TradeTalk',
    key: 'navigation:breadcrumbs.trade_talk',
  },
  [PagePath.Charts]: {
    title: 'Charts',
    key: 'navigation:breadcrumbs.charts',
  },
  [PagePath.AssetsManagement]: {
    title: 'Assets Management',
    key: 'navigation:breadcrumbs.assets_management',
  },

  [PagePath.Root]: {
    title: 'Trader',
    key: 'navigation:breadcrumbs.trader',
  },
  [PagePath.Contests]: {
    title: 'Contests',
    key: 'navigation:breadcrumbs.contests',
  },
  [PagePath.ContestCategory]: {
    title: 'Contest Category',
    key: 'navigation:breadcrumbs.contest_category',
  },
  [PagePath.ThankYou]: {
    title: 'Thank You',
    key: 'navigation:breadcrumbs.thank_you',
  },
  [PagePath.Verification]: {
    title: 'Verification',
    key: 'navigation:breadcrumbs.verification',
  },
  [PagePath.EditProfile]: {
    title: 'Edit Profile',
    key: 'navigation:breadcrumbs.edit_profile',
  },
  [PagePath.Welcome]: {
    title: 'Welcome',
    key: 'navigation:breadcrumbs.welcome',
  },
  [PagePath.ContestOverview]: {
    title: 'Contest Dashboard',
    disabled: true,
    key: 'navigation:breadcrumbs.contest_overview',
  },
  [PagePath.Purchase]: {
    title: 'Purchase',
    key: 'navigation:breadcrumbs.purchase',
  },
  [PagePath.AfterPurchase]: {
    title: 'After Purchase',
    key: 'navigation:breadcrumbs.after_purchase',
  },
  [PagePath.Coupons]: {
    title: 'Coupons',
    key: 'navigation:breadcrumbs.coupons',
  },
  [PagePath.Dashboard]: {
    title: 'Dashboard',
    key: 'navigation:breadcrumbs.dashboard',
    disabled: true,
  },
  [PagePath.Blacklisted]: {
    title: 'Blacklisted',
    key: 'navigation:breadcrumbs.blacklisted',
    disabled: true,
  },
  [PagePath.PrivacyOptOut]: {
    title: 'Privacy Opt-Out',
    key: 'navigation:breadcrumbs.privacy_opt_out',
  },
  [PagePath.CommunityStats]: {
    title: 'Community Stats',
    key: 'navigation:breadcrumbs.community_stats',
  },
};

/**
 *
 */
export const findBreadCrumb = (path?: string): PagePath | undefined => {
  return Object.keys(BreadcrumbElements).find(
    (key) => key === '/' + path,
  ) as PagePath;
};

/**
 *
 */
const Breadcrumbs = () => {
  const location = useLocation();
  const { t } = useTranslation('navigation');
  const items = location.pathname.split('/').map((path) => {
    const breadCrumb = findBreadCrumb(path);
    let key;
    let title = path;

    if (breadCrumb) {
      key = BreadcrumbElements[breadCrumb].key;
      title = BreadcrumbElements[breadCrumb].title;
    }

    return {
      path,
      title: key ? t(key, { defaultValue: title }) : title,
    };
  });

  return (
    <StyledBreadcrumbsContainer>
      <Breadcrumb
        itemRender={(currentPath, _, fullPath) => (
          <BreadcrumbLink currentPath={currentPath} fullPath={fullPath} />
        )}
        items={items}
      />
    </StyledBreadcrumbsContainer>
  );
};
export default Breadcrumbs;
